import { ChangeEvent, ElementType, useState } from "react";
import { Box, Typography, Button, Link, Paper, Stack, TextField } from "@mui/material";
import { ArrowBack, OpenInNew, School } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { Notification } from '@/components/Notification';


interface UploadTrainingProps {
    isLoading?: string;
    error?: string;
    clearError: () => void;
    setUploading: (uploading: boolean) => void;
    uploadTraining: (name: string, version: string) => Promise<string | undefined>;
}

const UploadTraining: ElementType<UploadTrainingProps> = ({ isLoading, error, clearError, setUploading, uploadTraining }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [isFocused, setIsFocused] = useState(false);
    const [name, setName] = useState('');
    const [form, setForm] = useState({
        name: '',
        version: ''
    });
    const isEmpty = name.length === 0;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
        const parts = e.target.value.split(':');
        const modelName = parts[0].trim();
        const version = parts.length > 1 && parts[1] ? parts[1].trim() : 'latest';
        
        setForm({
            name: modelName,
            version: version
        });
        
    };

    const getHelperText = () => {
        if (isEmpty) {
            return "required";
        }
        if (isFocused && name.length > 0) {
            return `Press Enter to upload model ${form.name}:${form.version} (${name.length}/50 characters)`;
        }
        return ' '; // empty string 
    };

    const handleSubmit = async (e: React.KeyboardEvent) => {
        e.preventDefault();
        if (isEmpty) {
            return;
        }
        const id = await uploadTraining(form.name, form.version);
        if (id) {
            navigate(`/trainings/${id}`);
        }
        setUploading(false);
    };

    return (
        <Box sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            p: theme.spacing(3),
        }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                gap: 2,
            }}>
                <School sx={{
                    fontSize: '2.2rem',
                    color: 'primary.main'
                }} />
                <TextField
                    value={name}
                    required
                    onChange={handleChange}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSubmit(e);
                    }}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    variant="standard"
                    placeholder="Enter Training name..."
                    inputProps={{
                        maxLength: 50,
                    }}
                    error={isFocused && isEmpty}
                    helperText={getHelperText()}
                    sx={{
                        flexGrow: 1,
                        '& .MuiInput-input': {
                            fontSize: '1.5rem',
                            fontWeight: 400,
                            padding: '4px 0',
                        },
                        // Hide bottom border when has value and not focused
                        '& .MuiInput-root:before': {
                            borderBottom: !isEmpty && !isFocused ? 'none' : undefined,
                        },
                        '& .MuiInput-root:hover:before': {
                            borderBottom: !isEmpty && !isFocused ? 'none !important' : undefined,
                        },
                    }}
                />
            </Box>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                p: theme.spacing(3)
            }}>
                <Paper sx={{
                    p: theme.spacing(4),
                    maxWidth: 600,
                    textAlign: 'center'
                }}>
                    <Stack spacing={3}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Upload Training Model
                        </Typography>

                        <Typography variant="body1" color="text.secondary">
                            To upload a training model, you'll need to browse the Ollama Library to find and download an LLM model.
                        </Typography>

                        <Box>
                            <Link
                                href="https://ollama.com/library"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                <Typography variant="h6" color="primary">
                                    Visit Ollama Library
                                </Typography>
                                <OpenInNew fontSize="small" />
                            </Link>
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                            Browse through the available models and follow the installation instructions for your chosen LLM model.
                        </Typography>

                        <Box sx={{ mt: 4 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBack />}
                                onClick={() => setUploading(false)}
                                size="large"
                            >
                                Back to Trainings
                            </Button>
                        </Box>
                    </Stack>
                </Paper>
            </Box>
            <Notification
                isLoading={isLoading}
                clearError={clearError}
                error={error}
                delay={300}
                autoHideDuration={1200}
            />  
        </Box>
    );
};

export default UploadTraining;